<script lang="ts">
  import { DailyLogo, NavigateButton, Slider } from "@opera-gx/components";
  import { IntersectionObserver } from "@opera-gx/components/v6";
  import { onMount } from "svelte";

  import { isIos } from "../../env";
  import DailyCastle from "./DailyCastle.svelte";
  import ReactionWrapper from "./ReactionWrapper.svelte";

  export let data;

  let innerWidth = window.innerWidth;
  let innerHeight = window.innerHeight;

  function handleOrientationChange() {
    innerWidth = window.innerWidth;
    innerHeight = window.innerHeight;

    document.documentElement.style.setProperty(
      "--page-heigth",
      innerHeight.toString() + "px",
    );
  }

  onMount(() => {
    handleOrientationChange();
    screen.orientation.addEventListener("change", handleOrientationChange);

    return () =>
      screen.orientation.removeEventListener("change", handleOrientationChange);
  });

  type Section = {
    id: number;
    key: string;
    publishedAt: string;
  };
  const contentSectionTypes = new Map([
    ["daily.daily-meme", async () => (await import("./MemeSection.svelte")).default],
    [
      "daily.daily-regular-content",
      async () => (await import("./DailyRegular.svelte")).default,
    ],
    [
      "daily.daily-this-or-that",
      async () => (await import("./DailyPoll.svelte")).default,
    ],
    ["daily.daily-short", async () => (await import("./DailyShort.svelte")).default],
  ]);

  function createMapFromJSON(jsonData) {
    const result: Section[] = [];

    const items = jsonData;

    items.forEach((item) => {
      const id = item.id;
      const key = item.key;
      const publishedAt = item.publishedAt;

      result.push({ id, key, publishedAt });
    });

    return result;
  }
  const sections = createMapFromJSON(data);

  let container: HTMLDivElement;
  let sectionWidth: HTMLDivElement;
  let currentItemIndex = 0;

  const calculateIndex = (container: HTMLDivElement) => {
    if (!container) {
      return;
    }

    currentItemIndex = Math.floor(container.scrollLeft / sectionWidth.clientWidth);
  };

  const handleScrollClick = () => {
    container.scrollBy(innerWidth, 0);
  };
  const handleScrollBackClick = () => {
    container.scrollBy(-innerWidth, 0);
  };
  const handleFastScrollClick = () => {
    container.scrollLeft = sectionWidth.clientWidth * (container.childElementCount - 1);
  };
  const handleFastScrollBackClick = () => {
    container.scrollLeft = 0;
  };

  // TODO: add proper typing
  const resolveContentType = (type: string) => {
    if (!contentSectionTypes.has(type)) {
      return () => null;
    }

    return contentSectionTypes.get(type);
  };

  const resolveReactionsLayout = (dailyType: string, description: string) => {
    if (dailyType === "daily.daily-short" && description) {
      return "buttonCenter";
    } else if (dailyType === "daily.daily-this-or-that" && innerWidth <= 600) {
      return "reactionsRight";
    } else {
      return "regular";
    }
  };
</script>

<style lang="scss">
  .daily-section {
    height: 560px;
    mask-border-source: url("./assets/SectionShape.svg");
    mask-border-slice: 20 fill;
    mask-border-width: 20px;

    @media (width >= 600px), (orientation: landscape) {
      height: var(--page-heigth);
    }
  }

  .daily-container {
    position: relative;
    max-height: 660px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-grow: 1;
  }

  .daily-background {
    position: absolute;
    inset: -20px 0 -10px;
    z-index: -1;
    background: radial-gradient(89.93% 111.8% at 64.21% 30.71%, #1c1726 0%, #2e273f 100%);
    opacity: 0.5;
    border-radius: 10px;
  }

  .logo-wrapper {
    width: 80px;
    height: 23px;
    position: relative;
    top: -10px;
    left: 10px;
  }

  .content-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    max-height: 560px;
    display: flex;
    align-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    background: linear-gradient(
      110.81deg,
      hsl(var(--color-N04)) 4.72%,
      hsl(var(--color-N08)) 29.23%,
      hsl(var(--color-N12)) 46.54%,
      hsl(var(--color-N12)) 50.37%,
      hsl(var(--color-N08)) 68.23%,
      hsl(var(--color-N04)) 105.53%
    );
  }

  .consistent-container {
    scroll-snap-align: center;
    width: 100%;
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
    top: 30px;
  }

  .slider {
    position: absolute;
    right: 10px;
    z-index: 1;
  }

  .reactions-container {
    position: sticky;
    bottom: -16px;
    margin-top: 10px;
    z-index: 1;
  }

  .controls-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 24px;
  }
</style>

<svelte:window bind:innerWidth bind:innerHeight />

<div class="daily-container">
  <div class="daily-background"></div>
  <div class="logo-wrapper">
    <DailyLogo />
  </div>
  <div class="daily-section">
    <div class="slider">
      <Slider
        activeSectionKey={sections[currentItemIndex]?.key}
        {sections}
        horizontal={true} />
    </div>

    <div
      class="content-wrapper daily-horizontal-scroll flex flex-col"
      style="--daily-scroll-padding:{isIos ? '5px' : '10px'}"
      on:scroll={() => {
        calculateIndex(container);
      }}
      bind:this={container}>
      {#each data as section}
        <div
          class="consistent-container snap-center snap-always"
          bind:this={sectionWidth}>
          <IntersectionObserver
            once={(isIos && section.dailyType === "daily.daily-short") ||
            section.dailyType === "daily.daily-regular-content"
              ? true
              : false}
            threshold={0.95}
            let:intersecting
            margin="50% 0px"
            class="flex-1">
            <div class="consistent-container">
              {#if intersecting}
                {#await resolveContentType(section.dailyType)() then componentResolved}
                  <svelte:component
                    this={componentResolved}
                    data={section}
                    postKey={section?.key} />
                  {#if section?.reactions}
                    <div class="reactions-container flex w-full">
                      <ReactionWrapper
                        reactions={section?.reactions}
                        minimode={section.dailyType === "daily.daily-regular-content"
                          ? 1300
                          : 600}
                        postKey={section?.key}
                        layout={resolveReactionsLayout(
                          section.dailyType,
                          section.description,
                        )}
                        button={section.dailyType === "daily.daily-short" &&
                        (!section.description ||
                          innerWidth >= 600 ||
                          innerWidth >= innerHeight)
                          ? { url: section.url, label: section.label }
                          : null} />
                    </div>
                  {/if}
                {/await}
              {/if}
            </div>
          </IntersectionObserver>
        </div>
      {/each}
      <div class="consistent-container">
        <DailyCastle />
      </div>
    </div>
  </div>
  <div class="controls-wrapper">
    <NavigateButton
      variant={"fastScroll"}
      rotation={270}
      on:click={handleFastScrollBackClick}
      disable={currentItemIndex === 0}
      platform={"mobile"} />
    <NavigateButton
      variant={"scroll"}
      rotation={270}
      on:click={handleScrollBackClick}
      disable={currentItemIndex === 0}
      platform={"mobile"} />
    <NavigateButton
      variant={"scroll"}
      rotation={90}
      on:click={handleScrollClick}
      disable={currentItemIndex === data.length}
      platform={"mobile"} />
    <NavigateButton
      variant={"fastScroll"}
      rotation={90}
      on:click={handleFastScrollClick}
      disable={currentItemIndex === data.length - 1 || currentItemIndex === data.length}
      platform={"mobile"} />
  </div>
</div>
